import {
  type BackgroundRender,
  type BannerRender,
  type CharacterRender,
  type ExplosionRender,
  type FrameRender,
  type HatRender,
  type Slot,
  type TrailRender,
  CUSTOM_ACCESS_ID,
  getItem,
  isCustomItemId,
} from "../../../shared/shop-catalog.js";
import { SHOP_CACHE_KEY } from "../../config";
import { dominantColor } from "../entities/CustomArt";
import { CustomStore, type CustomSlot } from "./CustomStore";
import type { Settings } from "./Settings";

const API_BASE = "/api";

export interface ShopState {
  wallet: number;
  owned: string[];
  /** Whether this player has mod-menu (admin) access — owner or owner-granted. */
  admin: boolean;
  equipped: Record<Slot, string | null>;
}

const EMPTY: ShopState = {
  wallet: 0,
  owned: [],
  admin: false,
  equipped: {
    character: null,
    banner: null,
    hat: null,
    trail: null,
    frame: null,
    background: null,
    background_night: null,
    explosion: null,
  },
};

interface CachedShop {
  name: string;
  state: ShopState;
}

export class Shop {
  private state: ShopState;
  /** Client-side store for the player's CUSTOM (hand-drawn) cosmetics. */
  readonly custom: CustomStore;

  constructor(
    private readonly settings: Settings,
    private readonly storage: Storage = window.localStorage,
  ) {
    this.state = this.loadCache() ?? EMPTY;
    this.custom = new CustomStore(settings, storage);
  }

  get wallet(): number {
    return this.state.wallet;
  }

  /** True if this player can open the mod menu (owner or owner-granted). */
  isAdmin(): boolean {
    return this.state.admin;
  }

  /** True if this player owns the CUSTOM collection (admin-granted). */
  hasCustomAccess(): boolean {
    return this.owns(CUSTOM_ACCESS_ID);
  }

  owns(itemId: string): boolean {
    return this.state.owned.includes(itemId);
  }

  equippedId(slot: Slot): string | null {
    return this.state.equipped[slot];
  }

  snapshot(): ShopState {
    return {
      wallet: this.state.wallet,
      owned: this.state.owned.slice(),
      admin: this.state.admin,
      equipped: { ...this.state.equipped },
    };
  }

  clear(): void {
    this.state = EMPTY;
    this.custom.reload();
    try {
      this.storage.removeItem(SHOP_CACHE_KEY);
    } catch {
      // ignore
    }
  }

  async refresh(): Promise<void> {
    if (!this.settings.isLoggedIn()) {
      this.state = EMPTY;
      return;
    }
    this.custom.reload();
    try {
      const res = await fetch(
        `${API_BASE}/shop/me?name=${encodeURIComponent(this.settings.playerName)}`,
        { headers: { authorization: `Bearer ${this.settings.authToken}` } },
      );
      if (res.status === 401) {
        this.handleExpiry();
        return;
      }
      if (!res.ok) return;
      const data = (await res.json()) as Partial<ShopState>;
      this.apply(data);
    } catch {
      // network errors leave cache untouched
    }
  }

  /**
   * The stored token no longer matches the server (rotated by a re-login
   * elsewhere, a password reset, etc.). Drop it so the UI shows the logged-out
   * state and the player can re-authenticate, instead of silently failing every
   * shop action with a valid-looking but dead token.
   */
  private handleExpiry(): void {
    this.settings.logout();
    this.clear();
  }

  async buy(itemId: string): Promise<{ ok: boolean; error?: string }> {
    if (!this.settings.isLoggedIn()) return { ok: false, error: "not_logged_in" };
    try {
      const res = await fetch(`${API_BASE}/shop/buy`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${this.settings.authToken}`,
        },
        body: JSON.stringify({ name: this.settings.playerName, itemId }),
      });
      if (res.status === 401) {
        this.handleExpiry();
        return { ok: false, error: "session_expired" };
      }
      const data = (await res.json().catch(() => ({}))) as Partial<ShopState> & {
        error?: string;
      };
      if (!res.ok) return { ok: false, error: data.error ?? "error" };
      this.apply(data);
      return { ok: true };
    } catch {
      return { ok: false, error: "network" };
    }
  }

  async equip(slot: Slot, itemId: string | null): Promise<{ ok: boolean; error?: string }> {
    if (!this.settings.isLoggedIn()) return { ok: false, error: "not_logged_in" };
    // Custom (hand-drawn) items live client-side and the server doesn't know
    // them, so equipping one is a purely local toggle. Equipping anything else
    // clears the local custom override for that slot first.
    if (itemId !== null && isCustomItemId(itemId)) {
      this.custom.setEquipped(slot as CustomSlot, true);
      return { ok: true };
    }
    this.custom.setEquipped(slot as CustomSlot, false);
    try {
      const res = await fetch(`${API_BASE}/shop/equip`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${this.settings.authToken}`,
        },
        body: JSON.stringify({ name: this.settings.playerName, slot, itemId }),
      });
      if (res.status === 401) {
        this.handleExpiry();
        return { ok: false, error: "session_expired" };
      }
      const data = (await res.json().catch(() => ({}))) as Partial<ShopState> & {
        error?: string;
      };
      if (!res.ok) return { ok: false, error: data.error ?? "error" };
      this.apply(data);
      return { ok: true };
    } catch {
      return { ok: false, error: "network" };
    }
  }

  getEquippedCharacterRender(): CharacterRender | null {
    const c = this.customDesign("character");
    if (c) return { kind: "custom", cells: c.cells, grid: c.grid };
    const item = getItem(this.equippedId("character"));
    return item && item.slot === "character" ? item.render : null;
  }

  getEquippedBannerRender(): BannerRender | null {
    const c = this.customDesign("banner");
    if (c) {
      return { kind: "custom", a: dominantColor(c.cells), cells: c.cells, grid: c.grid, animated: !!c.animated };
    }
    const item = getItem(this.equippedId("banner"));
    return item && item.slot === "banner" ? item.render : null;
  }

  getEquippedHatRender(): HatRender | null {
    const c = this.customDesign("hat");
    if (c) return { kind: "custom", cells: c.cells, grid: c.grid };
    const item = getItem(this.equippedId("hat"));
    return item && item.slot === "hat" ? item.render : null;
  }

  getEquippedTrailRender(): TrailRender | null {
    const c = this.customDesign("trail");
    if (c) {
      return {
        kind: "custom",
        color: dominantColor(c.cells),
        sizeMin: 3,
        sizeMax: 5,
        vy: -8,
        lifeMin: 0.5,
        lifeMax: 0.8,
        ratePerSec: 14,
        cells: c.cells,
        grid: c.grid,
      };
    }
    const item = getItem(this.equippedId("trail"));
    return item && item.slot === "trail" ? item.render : null;
  }

  getEquippedFrameRender(): FrameRender | null {
    const c = this.customDesign("frame");
    if (c) {
      return {
        kind: "custom",
        color: dominantColor(c.cells),
        width: 3,
        cells: c.cells,
        grid: c.grid,
        animated: !!c.animated,
        animColors: c.animColors ?? [],
      };
    }
    const item = getItem(this.equippedId("frame"));
    return item && item.slot === "frame" ? item.render : null;
  }

  getEquippedBackgroundRender(night = false): BackgroundRender | null {
    // The CUSTOM editor only draws a single (day) scenery; it applies to both phases.
    if (!night) {
      const c = this.customDesign("background");
      if (c) {
        return {
          kind: "custom",
          skyTop: "#000",
          skyBottom: "#000",
          treeFar: "#000",
          treeNear: "#000",
          cloud: "rgba(255,255,255,0.5)",
          sun: "#fff",
          isNight: false,
          cells: c.cells,
          grid: c.grid,
        };
      }
    }
    const item = getItem(this.equippedId(night ? "background_night" : "background"));
    if (item && (item.slot === "background" || item.slot === "background_night")) {
      return item.render;
    }
    return null;
  }

  getEquippedExplosionRender(): ExplosionRender | null {
    const c = this.customDesign("explosion");
    if (c) return { kind: "custom", color: dominantColor(c.cells), cells: c.cells, grid: c.grid };
    const item = getItem(this.equippedId("explosion"));
    return item && item.slot === "explosion" ? item.render : null;
  }

  /** The active hand-drawn design for a slot, or null when none is equipped. */
  private customDesign(
    slot: CustomSlot,
  ): { cells: string[]; grid: number; animated?: boolean; animColors?: string[] } | null {
    if (!this.custom.isEquipped(slot)) return null;
    return this.custom.design(slot);
  }

  private apply(data: Partial<ShopState>): void {
    const equipped = data.equipped ?? this.state.equipped;
    this.state = {
      wallet: Math.max(0, Number(data.wallet ?? this.state.wallet) || 0),
      owned: Array.isArray(data.owned) ? data.owned.slice() : this.state.owned.slice(),
      admin: data.admin ?? this.state.admin,
      equipped: {
        character: equipped.character ?? null,
        banner: equipped.banner ?? null,
        hat: equipped.hat ?? null,
        trail: equipped.trail ?? null,
        frame: equipped.frame ?? null,
        background: equipped.background ?? null,
        background_night: equipped.background_night ?? null,
        explosion: equipped.explosion ?? null,
      },
    };
    this.saveCache();
  }

  private loadCache(): ShopState | null {
    if (!this.settings.isLoggedIn()) return null;
    try {
      const raw = this.storage.getItem(SHOP_CACHE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw) as Partial<CachedShop>;
      if (data?.name !== this.settings.playerName || !data.state) return null;
      const s = data.state;
      return {
        wallet: Math.max(0, Number(s.wallet) || 0),
        owned: Array.isArray(s.owned) ? s.owned.slice() : [],
        admin: Boolean(s.admin),
        equipped: {
          character: s.equipped?.character ?? null,
          banner: s.equipped?.banner ?? null,
          hat: s.equipped?.hat ?? null,
          trail: s.equipped?.trail ?? null,
          frame: s.equipped?.frame ?? null,
          background: s.equipped?.background ?? null,
          background_night: s.equipped?.background_night ?? null,
          explosion: s.equipped?.explosion ?? null,
        },
      };
    } catch {
      return null;
    }
  }

  private saveCache(): void {
    if (!this.settings.isLoggedIn()) return;
    try {
      const payload: CachedShop = { name: this.settings.playerName, state: this.state };
      this.storage.setItem(SHOP_CACHE_KEY, JSON.stringify(payload));
    } catch {
      // ignore
    }
  }
}

